/**
 * Lien waiver documents — POST/GET /api/projects/:id/lien-docs, PDF download URL.
 */

import { apiFetch, resolveApiUrl } from './client'

export type LienDocType =
  | 'conditional_waiver'
  | 'unconditional_waiver'
  | 'conditional_final_waiver'
  | 'unconditional_final_waiver'

export interface CreateLienDocInput {
  doc_type: LienDocType
  signatory_name: string
  signatory_title?: string
  through_date?: string
  amount?: number
  maker_of_check?: string
  check_payable_to?: string
  pay_app_id?: number
  jurisdiction?: string
}

export interface LienDocRecord {
  id: number
  project_id: number
  pay_app_id: number | null
  doc_type: LienDocType | string
  filename: string
  signatory_name: string
  signatory_title: string | null
  through_date: string | null
  amount: string | null
  created_at: string
  jurisdiction: string | null
}

export async function createLienDoc(
  projectId: number,
  input: CreateLienDocInput,
): Promise<LienDocRecord> {
  return apiFetch<LienDocRecord>('POST', `/api/projects/${projectId}/lien-docs`, {
    jurisdiction: 'california',
    ...input,
  })
}

export async function getLienDocs(projectId: number): Promise<LienDocRecord[]> {
  return apiFetch<LienDocRecord[]>('GET', `/api/projects/${projectId}/lien-docs`)
}

export function getLienDocPdfUrl(lienDocId: number, token: string): string {
  const q = encodeURIComponent(token)
  return resolveApiUrl(`/api/lien-docs/${lienDocId}/pdf?token=${q}`)
}

export const LIEN_DOC_TYPE_LABELS: Record<string, string> = {
  conditional_waiver: 'Conditional Waiver – Progress Payment',
  unconditional_waiver: 'Unconditional Waiver – Progress Payment',
  conditional_final_waiver: 'Conditional Waiver – Final Payment',
  unconditional_final_waiver: 'Unconditional Waiver – Final Payment',
}

export interface UnconditionalLienStatusRow {
  pay_app_id: number
  app_number: number
  period_label: string | null
  paid: boolean
  blocked_reason: string | null
  doc_type: LienDocType
  existing_lien_doc_id: number | null
  existing_doc_type: string | null
  through_date: string
}

export async function fetchUnconditionalLienStatus(
  projectId: number,
): Promise<UnconditionalLienStatusRow[]> {
  return apiFetch<UnconditionalLienStatusRow[]>(
    'GET',
    `/api/projects/${projectId}/unconditional-lien-status`,
  )
}

export async function generateUnconditionalLien(
  projectId: number,
  payAppId: number,
): Promise<LienDocRecord> {
  return apiFetch<LienDocRecord>(
    'POST',
    `/api/projects/${projectId}/pay-apps/${payAppId}/unconditional-lien`,
  )
}

export async function sendLienDocToOwner(
  lienDocId: number,
  to?: string,
): Promise<{ sent: boolean; to?: string; dev?: boolean }> {
  return apiFetch<{ sent: boolean; to?: string; dev?: boolean }>(
    'POST',
    `/api/lien-docs/${lienDocId}/email`,
    to ? { to } : {},
  )
}
